/* eslint-disable no-self-assign */
// React imports
import { useState, useMemo, useEffect } from 'react';
import { useOutletContext, useNavigate, useLocation, useSearchParams } from 'react-router-dom';

// Generic imports
import _ from 'lodash';
import axios from 'axios';
import dayjs from 'dayjs';
import { v4 as uuidv4 } from 'uuid';
import pluralize from 'pluralize';

// Material UI imports
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import { grey, amber, red, green } from '@mui/material/colors';
import CircularProgress from '@mui/material/CircularProgress';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import { useTheme } from '@mui/material/styles';

// Font Awesome imports
import { faTruckFast, faPlus, faTable, faPenToSquare, faSquareList, faSquareKanban, faTrashCan, faTrashCanUndo } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWpforms } from '@fortawesome/free-brands-svg-icons';

// Local imports
import { calcChargeableWeight, calcWeightOrMeasurment } from './CalcWeight.js';
import Header from './Header.js';
import OpportunityRow from './OpportunityRow.js';
import FlowWelcomeKanban from './FlowWelcome.kanban.js';
import OpportunitiesFilter, { applyFilter } from './dashboard/components/OpportunitiesFilter.js';
import { restoreStatus } from './utils/opportunity.js';
import { calcOfferSellAndCost } from './utils/opportunityFinancials.js';
import currencyToText from './CurrencyToText.js';
import IconButton from '@mui/material/IconButton';
import { faPaperPlane, faCheck, faCircleCheck } from '@fortawesome/pro-solid-svg-icons';
import readableId from './ReadableId.js';
import SendOpportunityDialog from './dashboard/components/SendOpportunityDialog.js';
import FollowUpDoneDialog, { isFollowUpDone } from './FollowUpDoneDialog.js';
import { buildFollowUpEmail, followUpRoute, sentTimestamp } from './followUp.js';

function FlowWelcome(props) {
    const { org, opportunities, accesstoken, onUpdateOpportunity, me, accounts, featureToggle, exchangeRates, emailLoginInfo, onEmailRefreshConnection, onEmailLogout } = useOutletContext();
    return (
        <FlowWelcomeProps {...props} org={org} opportunities={opportunities} accesstoken={accesstoken} onUpdateOpportunity={onUpdateOpportunity} me={me} accounts={accounts} featureToggle={featureToggle} exchangeRates={exchangeRates} emailLoginInfo={emailLoginInfo} onEmailRefreshConnection={onEmailRefreshConnection} onEmailLogout={onEmailLogout} />
    );
}

function FlowWelcomeProps(props) {
    const __tRender0 = (typeof performance !== 'undefined' ? performance.now() : Date.now());
    const { baseDaysBack = 7, noTotal = false, drawerType = 'shipment', noHeader = false,
        org, opportunities, accesstoken, onUpdateOpportunity, me, accounts, featureToggle, exchangeRates,
        emailLoginInfo, onEmailRefreshConnection, onEmailLogout } = props;
    const [searchParams] = useSearchParams();
    const filterQuery = searchParams.get('filter');
    const theme = useTheme();
    const filterStorage = filterQuery ? decodeURIComponent(atob(filterQuery)) : sessionStorage.getItem('FlowWelcome.main.opportunities');
    const baseFilter = (() => {
        const base = { days: baseDaysBack, teamMember: me.email, accounts: [], openTasksFilter: [], rateRequest: 'all', taskStatus: 'all', taskType: 'all', tags: [], freetext: '', days: 'week' };
        try {
            return filterStorage ? JSON.parse(filterStorage) : base;
        } catch (e) {
            return base;
        }
    })();
    const [ filter, setFilter ] = useState(baseFilter);

    const [pageNumber, setPageNumber] = useState(1);
    const navigate = useNavigate();
    const location = useLocation();
    const lf = new Intl.ListFormat('en');

    const filteredOpportunities = (opportunities || []).filter(r => r.status !== 'deleted');

    const handleScroll = () => {
        const bottom =
            window.innerHeight + window.scrollY >= document.body.offsetHeight - 20;
        if (bottom) {
            setPageNumber(prevPageNumber => {
                if (prevPageNumber * 20 > filteredOpportunities.length) return prevPageNumber;
                return prevPageNumber + 1
            });
        }
    };

    useEffect(() => {
        window.addEventListener('scroll', handleScroll);
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [ opportunities ]);

    const canSeeFinancials = !!me?.showFinancials || me?.email === 'admin@monada.ai';

    // Financial totals (must be before early returns to satisfy hooks rules)
    const financials = useMemo(() => {
        if (!canSeeFinancials || !exchangeRates || !org || !opportunities) return null;
        const filtered = applyFilter({ filter, opportunities: (opportunities || []).filter(r => r.status !== 'deleted'), org, accounts: accounts || [] });
        if (!filtered.length) return null;
        const cur = org.defaults?.currency || 'USD';
        const fmt = new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
        let totalSell = 0, totalCost = 0;
        for (const o of filtered) {
            const selectedOffer = o.offers?.find(o2 => o.selected?.includes(o2.id)) || o.offers?.[0];
            if (!selectedOffer) continue;
            const { sell, cost } = calcOfferSellAndCost({ offer: selectedOffer, opportunity: o, exchangeRates, organization: org });
            if (isFinite(sell) && !isNaN(sell)) totalSell += sell;
            if (isFinite(cost) && !isNaN(cost)) totalCost += cost;
        }
        const gp = totalSell - totalCost;
        const margin = totalSell > 0 ? (gp / totalSell) * 100 : 0;
        return {
            sell: currencyToText(cur, true, fmt.format(totalSell)),
            cost: currencyToText(cur, true, fmt.format(totalCost)),
            gp: currencyToText(cur, true, fmt.format(gp)),
            margin: `${new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 }).format(margin)}%`,
            gpPositive: gp >= 0,
        };
    }, [opportunities, exchangeRates, org, filter, accounts]);

    const [ sortBy, setSortBy ] = useState('date');
    const [ view, _setView ] = useState(sessionStorage.getItem('FlowWelcome.view') || 'list');
    const setView = (v) => { _setView(v); sessionStorage.setItem('FlowWelcome.view', v); };
    const [ followUpExpanded, _setFollowUpExpanded ] = useState(() => sessionStorage.getItem('followUp.expanded') !== 'false');
    const setFollowUpExpanded = (v) => { const val = typeof v === 'function' ? v(followUpExpanded) : v; _setFollowUpExpanded(val); sessionStorage.setItem('followUp.expanded', String(val)); };
    const [ followUpSortAsc, _setFollowUpSortAsc ] = useState(() => sessionStorage.getItem('followUp.sortAsc') !== 'false');
    const setFollowUpSortAsc = (v) => { const val = typeof v === 'function' ? v(followUpSortAsc) : v; _setFollowUpSortAsc(val); sessionStorage.setItem('followUp.sortAsc', String(val)); };
    // Follow-up email composer state
    const [ followUp, setFollowUp ] = useState(null); // { opportunity, toRecipients, subject, body }
    const [ followUpAttachPDF, setFollowUpAttachPDF ] = useState(true);
    const [ followUpToast, setFollowUpToast ] = useState(null); // confirmation toast after send
    const [ followedUpIds, setFollowedUpIds ] = useState([]); // marked done this session (struck through until refresh)
    const [ followUpDone, setFollowUpDone ] = useState(null); // opportunity being marked as followed up outside Monada
    const [ restoringId, setRestoringId ] = useState(null); // deleted quote currently being restored
    const [ restoreToast, setRestoreToast ] = useState(null); // { severity, message } after a restore

    function openFollowUp(o) {
        const { toRecipients, subject, body } = buildFollowUpEmail({ opportunity: o, accounts });
        setFollowUpAttachPDF(true);
        setFollowUp({ opportunity: o, toRecipients, subject, body });
    }

    // Mark a quote as followed-up locally: it stays visible (struck through) until the next
    // refresh and stops counting toward the pending total. The durable 'Follow-up sent' tag +
    // log entry are written server-side by the email-queue worker (logFollowUp flag).
    function markFollowUpSent(o) {
        setFollowedUpIds(ids => ids.includes(o.id) ? ids : [...ids, o.id]);
    }

    // A follow-up done outside Monada (call, text, ...) is saved as a comment on the quote. A
    // comments-only update is allowed for any teammate, not just the quote's owner, and the
    // comment's `followUp` marker is what takes the quote out of the queue.
    async function saveFollowUpDone({ opportunity: o, update }) {
        try {
            const { data: saved } = await axios.put(`/api/opportunities/${o.id}?accesstoken=${accesstoken}&organizationId=${org.id}`, { opportunity: { updates: [...(o.updates || []), update] } });
            onUpdateOpportunity({ opportunity: saved });
        } catch (e) {
            return false;
        }
        markFollowUpSent(o);
        setFollowUpDone(null);
        setFollowUpToast(`Follow-up for quote ${readableId(o)} marked as done`);
        return true;
    }

    // Needs Follow-up: sent within the org's configured window (default 3–14 days) with no response.
    const followUpMinDays = _.get(org, 'settings.followUp.minDays', 3);
    const followUpMaxDays = _.get(org, 'settings.followUp.maxDays', 14);
    const needsFollowUp = useMemo(() => {
        if (!opportunities) return [];
        const list = filteredOpportunities.filter(o => {
            if (o.status !== 'sent-to-endcustomer' && o.status !== 'opened-by-customer' && o.status !== 'sending-to-endcustomer') return false;
            if ((o.tags || []).some(t => t.label === 'Follow-up sent')) return false; // already followed up
            if (isFollowUpDone(o)) return false; // followed up outside Monada
            if (!o.account?.id) return false;
            const accountExists = accounts?.find(a => a.id === o.account.id);
            if (!accountExists && !o.account?.name) return false;
            if (filter.members?.length > 0 && !filter.members.includes(o.owner)) return false;
            const sent = sentTimestamp(o);
            const daysSince = sent ? dayjs().startOf('day').diff(dayjs(sent).startOf('day'), 'day') : 0;
            return daysSince >= followUpMinDays && daysSince <= followUpMaxDays;
        });
        return _.sortBy(list, o => {
            const sent = sentTimestamp(o);
            return followUpSortAsc ? sent : -sent;
        });
    }, [opportunities, filteredOpportunities, filter.members, followUpSortAsc, accounts, followUpMinDays, followUpMaxDays]);

    // Follow-ups sent today: count quotes with a durable 'follow-up-sent' log entry stamped today
    // (written server-side by the email-queue worker), unioned with sends made this session so the
    // count updates instantly before the opportunities data refreshes from the server.
    const followUpsSentToday = useMemo(() => {
        const startOfDay = dayjs().startOf('day').valueOf();
        const ids = new Set(followedUpIds);
        for (const o of (filteredOpportunities || [])) {
            if (filter.members?.length > 0 && !filter.members.includes(o.owner)) continue;
            if ((o.log || []).some(l => l.action === 'follow-up-sent' && l.timestamp >= startOfDay)) ids.add(o.id);
            if ((o.updates || []).some(u => u && !u.deleted && u.followUp && u.created >= startOfDay)) ids.add(o.id);
        }
        return ids.size;
    }, [filteredOpportunities, filter.members, followedUpIds]);

    // Deleted quotes matching the current search. Deleting is a soft delete, so a quote someone
    // searches for by number or customer and can't find is often sitting here rather than gone —
    // surface it instead of returning nothing. Only computed while a search is active, so the
    // banner never shows up as background noise on the normal list.
    //
    // The search runs with the date window opened up and the status-shaped filters neutralised:
    // a quote is being looked for by name, and a 'this week' or 'open tasks only' filter that
    // structurally can't match a deleted quote would otherwise hide the very hint that explains
    // where it went. Every other filter (member, account, tags, route, freetext) still applies.
    const deletedMatches = useMemo(() => {
        if (!(filter.freetext || []).length) return [];
        const deleted = (opportunities || []).filter(r => r.status === 'deleted');
        if (!deleted.length) return [];
        return applyFilter({
            filter: { ...filter, days: 36500, dateFrom: undefined, dateTo: undefined, taskStatus: 'all', pipelineStage: null },
            opportunities: deleted,
            org,
            accounts: accounts || [],
        });
    }, [opportunities, filter, org, accounts]);

    async function onRestoreOpportunity(opportunity) {
        setRestoringId(opportunity.id);
        try {
            const { data: restored } = await axios.put(
                `/api/opportunities/${opportunity.id}?accesstoken=${accesstoken}&organizationId=${org.id}`,
                { opportunity: { status: restoreStatus(opportunity) } },
            );
            onUpdateOpportunity({ opportunity: restored });
            setRestoreToast({ severity: 'success', message: `Quote ${readableId(opportunity)} restored` });
        } catch (e) {
            setRestoreToast({ severity: 'error', message: `Couldn't restore quote ${readableId(opportunity)}. Please try again.` });
        } finally {
            setRestoringId(null);
        }
    }

    const unsortedOpportunities = applyFilter({ filter, opportunities: (opportunities || []).filter(r => r.status !== 'deleted'), org, accounts: accounts || [] });

    const mainOpportunities = useMemo(() => {
        if (!unsortedOpportunities) return [];
        if (sortBy === 'date' || !exchangeRates || !org) return unsortedOpportunities;
        const isAsc = sortBy.endsWith('-asc');
        const field = isAsc ? sortBy.replace('-asc', '') : sortBy;
        const withFinancials = unsortedOpportunities.map(o => {
            const selectedOffer = o.offers?.find(o2 => o.selected?.includes(o2.id)) || o.offers?.[0];
            if (!selectedOffer) return { ...o, _sell: 0, _cost: 0, _gp: 0, _margin: 0 };
            const { sell, cost } = calcOfferSellAndCost({ offer: selectedOffer, opportunity: o, exchangeRates, organization: org });
            const _sell = isFinite(sell) && !isNaN(sell) ? sell : 0;
            const _cost = isFinite(cost) && !isNaN(cost) ? cost : 0;
            const gp = _sell - _cost;
            const margin = _sell > 0 ? (gp / _sell) * 100 : 0;
            return { ...o, _sell, _cost, _gp: gp, _margin: margin };
        });
        return _.orderBy(withFinancials, [`_${field}`], [isAsc ? 'asc' : 'desc']);
    }, [unsortedOpportunities, sortBy, exchangeRates, org]);

    if (!opportunities) {
        return (
            <Box sx={{ padding: '36px 0px', background: theme.palette.background.default, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress size={18} />
            </Box>
        );
    }

    if (!accounts) {
        return (
            <Box sx={{ padding: '36px 0px', background: theme.palette.background.default, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress size={18} />
            </Box>
        );
    }

    function setFilterWithStorage(newFilter) {
        if (typeof newFilter === 'function') {
            setFilter(prev => {
                const resolved = newFilter(prev);
                if (!filterQuery) {
                    sessionStorage.setItem('FlowWelcome.main.opportunities', JSON.stringify(resolved));
                }
                return resolved;
            });
        } else {
            if (!filterQuery) {
                sessionStorage.setItem('FlowWelcome.main.opportunities', JSON.stringify(newFilter));
            }
            setFilter(newFilter);
        }
    }

    function onOpen({ opportunity, newWindow }) {
        if (newWindow) {
            if (!location.pathname || location.pathname.length < 2) {
                window.open(`https://app.monada.ai/organization/${org.alias}/opportunities/${opportunity.id}/?accesstoken=${encodeURIComponent(accesstoken.monada)}`, '_blank');
            } else {
                window.open(`${location.pathname.replace(/\/main/, '')}/${opportunity.id}/?${location.search}`.replace(/\/\//g, '/'), '_blank');
            }
        } else {
            navigate(`/organization/${org.alias}/opportunities/${opportunity.id}/`);
        }
    }

    const sums = _(mainOpportunities)
        .map(opportunity => _(opportunity.products).map((p, i) => {
            const CW = calcChargeableWeight({
                product: p, transportationMethod: opportunity.transportationMethod, allProducts: opportunity.products, round: org.airRoundChargeableWeight });
            if (CW) {
                // Convert all chargeable weights to kg
                let valInKg = CW.val;
                if (CW.postfix === 'lbs') {
                    valInKg = CW.val * 0.453592; // Convert lbs to kg
                }
                return { type: 'chargeable Weight', value: { val: valInKg, postfix: 'kg' } };
            }

            const WM = calcWeightOrMeasurment({ 
                product: p, transportationMethod: opportunity.transportationMethod, allProducts: opportunity.products });
            if (WM) {
                // Convert weight or measurement to a single unit (ton)
                // For volume (cbm), convert to ton (1 cbm = 1 ton for freight calculations)
                // For weight (ton), keep as is
                let valInTon = WM.val;
                if (WM.postfix === 'cbm') {
                    valInTon = WM.val; // 1 cbm = 1 ton for freight calculations
                }
                return { type: 'weight or measurement', value: { val: valInTon, postfix: 'ton' } };
            }

            const qty = p.quantity || 1;
            const pType = (p.type || '').toLowerCase();
            const teus = pType.includes('40') ? qty * 2 : qty;
            return { type: 'TEUs', value: { val: teus, postfix: '' } };
        }).value()).
        flatten().
        groupBy(s => `${s.type}|${s.value.postfix}`).
        mapValues(v => _.sumBy(v, v2 => v2.value.val || 0)).
        value();

    const totals = _.map(sums, (v, k) => {
        const [type, postfix] = k.split('|');
        return `Total ${type.toLowerCase()}: <b>${v.toLocaleString()} ${postfix}</b>`;
    }).join(' | ');


    const __tRender1 = (typeof performance !== 'undefined' ? performance.now() : Date.now());
    const __dtRender = __tRender1 - __tRender0;
    if (__dtRender > 50) {
        console.log(`[perf] FlowWelcomeProps render body: ${__dtRender.toFixed(1)}ms  opps=${(opportunities || []).length}  filtered=${mainOpportunities.length}`);
    }

    function onNewOpportunity({ type }) {
        const id = uuidv4();
        onUpdateOpportunity({ opportunity: { status: 'draft', id, type, account: {}, offers: [], products: [], terms: [], created: new Date().getTime(), createdBy: me.email, owner: me.email }});
        navigate(`/organization/${org.alias}/opportunities/${id}/`);
    }

    return (
        <Box sx={{ paddingBottom: '36px', background: theme.palette.background.default }}>
            {!noHeader && (
                <Header
                    icon={faTruckFast}
                    title={'My Opportunities'}
                    buttons={[{
                        title: 'New Opportunity',
                        icon: faPlus,
                        color: 'monada-button',
                        submenu: [
                            {
                                icon: faPenToSquare,
                                title: 'Quick Quote (Text)',
                                action: () => onNewOpportunity({ type: 'one-off-free-text' }),
                            },
                            {
                                icon: faWpforms,
                                title: 'Form Builder',
                                action: () => onNewOpportunity({ type: 'one-off' }),
                            },
                            {
                                icon: faTable,
                                title: 'Contract',
                                action: () => onNewOpportunity({ type: 'contract' }),
                            },
                        ]
                    }]}
                />
            )}
            <Box style={{ padding: '12px', paddingTop: '0px' }}>

                <OpportunitiesFilter
                    filter={filter}
                    setFilter={setFilterWithStorage}
                    me={me}
                    opportunities={filteredOpportunities}
                    accounts={accounts}
                    org={org}
                    groupedDays={true}
                />

                {/* Deleted quotes matching the search — only rendered while searching */}
                {deletedMatches.length > 0 && (
                    <Paper sx={{ mt: '12px', border: `1px solid ${red[200]}`, background: red[50], borderRadius: '8px', overflow: 'hidden' }} elevation={0}>
                        <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <FontAwesomeIcon icon={faTrashCan} style={{ color: red[700], fontSize: '13px' }} />
                            <Box>
                                <Typography sx={{ fontSize: '13px', fontWeight: 700, color: red[900] }}>
                                    {deletedMatches.length} deleted {pluralize('quote', deletedMatches.length)} {deletedMatches.length === 1 ? 'matches' : 'match'} your search
                                </Typography>
                                <Typography sx={{ fontSize: '11px', color: red[800] }}>
                                    {deletedMatches.length === 1 ? 'It is' : 'They are'} hidden from the list below. Nothing was lost — restoring brings {deletedMatches.length === 1 ? 'it' : 'them'} back in full.
                                </Typography>
                            </Box>
                        </Box>
                        <Box sx={{ maxHeight: '200px', overflowY: 'auto' }}>
                            {deletedMatches.map(o => {
                                const accountName = accounts?.find(a => a.id === o.account?.id)?.name || o.account?.name || '';
                                const route = followUpRoute(o);
                                const deletion = _.findLast(o.log || [], l => l.action === 'deleted') || _.last(o.log || []);
                                const restoring = restoringId === o.id;
                                return (
                                    <Box key={o.id}
                                        sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 2, py: 1, borderTop: `1px solid ${red[100]}` }}
                                    >
                                        <Box
                                            onClick={() => onOpen({ opportunity: o })}
                                            sx={{ flex: 1, minWidth: 0, cursor: 'pointer' }}
                                        >
                                            <Typography sx={{ fontSize: '12px', fontWeight: 600, color: red[900] }}>
                                                {readableId(o)}{accountName ? ` · ${accountName}` : ''}
                                            </Typography>
                                            <Typography sx={{ fontSize: '11px', color: red[800], overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {route}
                                                {deletion?.timestamp ? ` · deleted ${dayjs(deletion.timestamp).format('MMM D, YYYY')}` : ''}
                                                {deletion?.modifiedBy ? ` by ${deletion.modifiedBy}` : ''}
                                            </Typography>
                                        </Box>
                                        <Button
                                            size='small'
                                            variant='outlined'
                                            disabled={restoring}
                                            startIcon={restoring
                                                ? <CircularProgress size={12} sx={{ color: red[700] }} />
                                                : <FontAwesomeIcon icon={faTrashCanUndo} style={{ fontSize: '12px' }} />}
                                            onClick={() => onRestoreOpportunity(o)}
                                            sx={{ flexShrink: 0, fontSize: '11px', textTransform: 'none', color: red[800], borderColor: red[300], '&:hover': { borderColor: red[500], background: red[100] } }}
                                        >
                                            {restoring ? 'Restoring…' : 'Restore'}
                                        </Button>
                                    </Box>
                                );
                            })}
                        </Box>
                    </Paper>
                )}

                <Snackbar
                    open={Boolean(restoreToast)}
                    autoHideDuration={5000}
                    onClose={() => setRestoreToast(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                    <Alert onClose={() => setRestoreToast(null)} severity={restoreToast?.severity || 'success'} variant='filled' sx={{ width: '100%' }}>
                        {restoreToast?.message}
                    </Alert>
                </Snackbar>

                {/* Needs Follow-up Banner — always shown, even when nothing needs follow-up */}
                {(() => {
                    const pending = needsFollowUp.filter(o => !followedUpIds.includes(o.id)).length;
                    const allClear = pending === 0;
                    return (
                    <Paper sx={{ mt: '12px', border: `1px solid ${allClear ? green[200] : amber[200]}`, background: allClear ? green[50] : amber[50], borderRadius: '8px', overflow: 'hidden' }} elevation={0}>
                        <Box
                            onClick={() => setFollowUpExpanded(e => !e)}
                            sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', '&:hover': { background: allClear ? green[100] : amber[100] } }}
                        >
                            <Box>
                                {allClear ? (
                                    <Typography sx={{ fontSize: '13px', fontWeight: 700, color: green[900] }}>
                                        All caught up — no quotes need follow-up
                                    </Typography>
                                ) : (
                                    <Typography sx={{ fontSize: '13px', fontWeight: 700, color: amber[900] }}>
                                        {pending} {pending === 1 ? 'quote needs' : 'quotes need'} follow-up
                                    </Typography>
                                )}
                                {followUpExpanded && !allClear && (
                                    <Typography sx={{ fontSize: '11px', color: amber[800] }}>
                                        Sent {followUpMinDays}–{followUpMaxDays} days ago without a response — consider reaching out
                                    </Typography>
                                )}
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Typography sx={{ fontSize: '11px', fontWeight: 600, color: green[700], whiteSpace: 'nowrap' }}>
                                    {followUpsSentToday} {followUpsSentToday === 1 ? 'follow-up' : 'follow-ups'} sent today
                                </Typography>
                                {followUpExpanded && !allClear && (
                                    <Typography
                                        sx={{ fontSize: '11px', color: amber[700], cursor: 'pointer', '&:hover': { color: amber[900] } }}
                                        onClick={(e) => { e.stopPropagation(); setFollowUpSortAsc(s => !s); }}
                                    >
                                        {followUpSortAsc ? 'Oldest first ↑' : 'Newest first ↓'}
                                    </Typography>
                                )}
                                <Typography sx={{ fontSize: '11px', color: allClear ? green[700] : amber[700] }}>{followUpExpanded ? '▲ Collapse' : '▼ Expand'}</Typography>
                            </Box>
                        </Box>
                        {followUpExpanded && allClear && (
                            <Box sx={{ px: 2, py: 1.5, borderTop: `1px solid ${green[100]}` }}>
                                <Typography sx={{ fontSize: '12px', color: green[800] }}>
                                    Nothing needs a follow-up right now. Quotes appear here {followUpMinDays}–{followUpMaxDays} days after being sent without a response.
                                </Typography>
                            </Box>
                        )}
                        {followUpExpanded && !allClear && <Box sx={{ maxHeight: '200px', overflowY: 'auto' }}>
                            {needsFollowUp.map(o => {
                                const sent = sentTimestamp(o);
                                const daysSince = sent ? dayjs().startOf('day').diff(dayjs(sent).startOf('day'), 'day') : 0;
                                const accountName = accounts?.find(a => a.id === o.account?.id)?.name || o.account?.name || '';
                                const urgencyColor = daysSince > 30 ? red[600] : daysSince > 14 ? red[400] : amber[800];
                                const done = followedUpIds.includes(o.id);
                                return (
                                    <Box key={o.id}
                                        onClick={() => onOpen({ opportunity: o })}
                                        sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 2, py: 1, cursor: 'pointer', borderTop: `1px solid ${amber[100]}`, opacity: done ? 0.55 : 1, '&:hover': { background: amber[100] } }}
                                    >
                                        <Tooltip title={`Sent ${daysSince} days ago — no response`} arrow>
                                            <Typography sx={{ fontSize: '12px', fontWeight: 700, color: urgencyColor, minWidth: '45px', textDecoration: done ? 'line-through' : 'none' }}>{daysSince}d</Typography>
                                        </Tooltip>
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography sx={{ fontSize: '12px', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textDecoration: done ? 'line-through' : 'none' }}>
                                                {accountName}
                                            </Typography>
                                        </Box>
                                        <Tooltip title='Quote status (not follow-up status)' arrow>
                                            <Typography sx={{ fontSize: '10px', color: '#94a3b8', whiteSpace: 'nowrap', textDecoration: done ? 'line-through' : 'none' }}>
                                                Quote {o.status === 'opened-by-customer' ? 'Opened' : 'Sent'}
                                            </Typography>
                                        </Tooltip>
                                        {done ? (
                                            <Tooltip title='Follow-up sent' arrow>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: green[700], pr: '4px' }}>
                                                    <FontAwesomeIcon icon={faCheck} style={{ fontSize: '11px' }} />
                                                    <Typography sx={{ fontSize: '10px', fontWeight: 600, whiteSpace: 'nowrap' }}>Sent</Typography>
                                                </Box>
                                            </Tooltip>
                                        ) : (
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <Tooltip title='Mark as followed up (by call, text, or outside Monada)' arrow>
                                                    <IconButton size='small' onClick={(e) => { e.stopPropagation(); setFollowUpDone(o); }} sx={{ color: amber[800], '&:hover': { color: green[700] } }}>
                                                        <FontAwesomeIcon icon={faCircleCheck} style={{ fontSize: '13px' }} />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title='Send a follow-up email to the customer' arrow>
                                                    <IconButton size='small' onClick={(e) => { e.stopPropagation(); openFollowUp(o); }} sx={{ color: amber[800], '&:hover': { color: amber[900] } }}>
                                                        <FontAwesomeIcon icon={faPaperPlane} style={{ fontSize: '12px' }} />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        )}
                                    </Box>
                                );
                            })}
                        </Box>}
                    </Paper>
                    );
                })()}

                {followUp && (
                    <Box sx={{
                        mt: 2,
                        border: `1px solid ${grey[300]}`,
                        borderRadius: '10px',
                        boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                        backgroundColor: '#fff',
                        overflow: 'hidden',
                    }}>
                        <SendOpportunityDialog
                            org={org}
                            accesstoken={accesstoken}
                            accounts={accounts}
                            emailLoginInfo={emailLoginInfo}
                            onEmailRefreshConnection={onEmailRefreshConnection}
                            onEmailLogout={onEmailLogout}
                            opportunity={followUp.opportunity}
                            toRecipients={followUp.toRecipients}
                            initialSubject={followUp.subject}
                            initialBody={followUp.body}
                            subjectEditable={true}
                            logFollowUp={true}
                            previewMode={/^(localhost|127\.0\.0\.1)$/.test(window.location.hostname) && emailLoginInfo?.status !== 'connected'}
                            attachPDF={followUpAttachPDF}
                            setAttachPDF={setFollowUpAttachPDF}
                            onClose={() => setFollowUp(null)}
                            onSent={(sentInfo) => {
                                const o = followUp.opportunity;
                                const customer = accounts?.find(a => a.id === o.account?.id)?.name || o.account?.name || '';
                                // Report the recipients the email was ACTUALLY sent to (the user may have
                                // edited the To line in the composer), not the original system-populated list.
                                const sentTo = sentInfo?.[0]?.toRecipients || followUp.toRecipients || [];
                                const to = sentTo.map(r => r.address || r).filter(Boolean).join(', ');
                                const who = customer ? `${customer}${to ? ` (${to})` : ''}` : to;
                                markFollowUpSent(o);
                                setFollowUp(null);
                                setFollowUpToast(`Follow-up email for quote ${readableId(o)} was sent${who ? ` to ${who}` : ''}`);
                            }}
                        />
                    </Box>
                )}

                {followUpDone && (
                    <FollowUpDoneDialog
                        opportunity={followUpDone}
                        quoteLabel={readableId(followUpDone)}
                        accountName={accounts?.find(a => a.id === followUpDone.account?.id)?.name || followUpDone.account?.name || ''}
                        me={me}
                        onSave={saveFollowUpDone}
                        onClose={() => setFollowUpDone(null)}
                    />
                )}

                <Snackbar
                    open={Boolean(followUpToast)}
                    autoHideDuration={5000}
                    onClose={() => setFollowUpToast(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                    <Alert onClose={() => setFollowUpToast(null)} severity='success' variant='filled' sx={{ width: '100%' }}>
                        {followUpToast}
                    </Alert>
                </Snackbar>

                {(mainOpportunities.length === 0) && (filter.teamMember === me.email) && (
                    <Box sx={{ paddingTop: '24px', textAlign: 'center' }}>
                        <Typography variant='body1'>
                            You don't have any opportunities.
                        </Typography>
                        <Typography variant='body1' sx={{ marginTop: '12px' }}>
                            Create Your First Opportunity
                        </Typography>
                    </Box>
                )}
                {(mainOpportunities.length > 0) && (
                    <Box>
                        <Paper sx={{ marginTop: '12px', border: `1px solid ${theme.palette.border1}`, background: theme.palette.panel1 }} elevation={0} >
                            <Typography variant='body2' sx={{ fontWeight: 800, padding: '12px', borderBottom: noTotal ? `1px solid ${theme.palette.border1}` : '' }}>
                                Opportunities
                            </Typography>
                            {!noTotal && (
                                <div style={{ padding: '6px 12px', paddingTop: '0px' }}>
                                    <Typography variant='caption'>
                                        Total of <b>{mainOpportunities.length.toLocaleString()} {pluralize('opportunity', mainOpportunities.length)}</b> | <span dangerouslySetInnerHTML={{ __html: totals }} />
                                    </Typography>
                                    {financials && (
                                        <Typography variant='caption' component='div' sx={{ mt: '2px' }}>
                                            Sell: <b>{financials.sell}</b> | Cost: <b>{financials.cost}</b> | GP: <b style={{ color: financials.gpPositive ? '#2e7d32' : '#d32f2f' }}>{financials.gp}</b> | Margin: <b style={{ color: financials.gpPositive ? '#2e7d32' : '#d32f2f' }}>{financials.margin}</b>
                                        </Typography>
                                    )}
                                    <Box sx={{ display: 'flex', gap: '12px', mt: '6px', alignItems: 'center' }}>
                                        {canSeeFinancials && [
                                            { key: 'date', label: 'Newest' },
                                            { key: 'sell', label: 'Sell' },
                                            { key: 'cost', label: 'Cost' },
                                            { key: 'gp', label: 'GP' },
                                            { key: 'margin', label: 'Margin' },
                                        ].map(opt => (
                                            <Typography
                                                key={opt.key}
                                                variant='caption'
                                                onClick={() => setSortBy(prev => prev === opt.key ? (opt.key === 'date' ? 'date' : `${opt.key}-asc`) : opt.key)}
                                                sx={{
                                                    cursor: 'pointer',
                                                    fontWeight: sortBy === opt.key || sortBy === `${opt.key}-asc` ? 700 : 500,
                                                    color: sortBy === opt.key || sortBy === `${opt.key}-asc` ? '#1976d2' : grey[500],
                                                    '&:hover': { color: '#1976d2' },
                                                    userSelect: 'none',
                                                }}
                                            >
                                                {opt.label} {sortBy === opt.key ? '↓' : sortBy === `${opt.key}-asc` ? '↑' : ''}
                                            </Typography>
                                        ))}
                                        <Box sx={{ flex: 1 }} />
                                        <Button
                                            variant='text'
                                            color='primary'
                                            onClick={() => setView('list')}
                                            sx={{ minWidth: 0, padding: 0, textTransform: 'none', fontSize: '11px !important', fontWeight: view === 'list' ? 800 : 400 }}
                                        >
                                            <FontAwesomeIcon icon={faSquareList} />&nbsp;List
                                        </Button>
                                        <Typography variant='caption' sx={{ fontSize: '11px' }}>/</Typography>
                                        <Button
                                            variant='text'
                                            color='primary'
                                            onClick={() => setView('kanban')}
                                            sx={{ minWidth: 0, padding: 0, textTransform: 'none', fontSize: '11px !important', fontWeight: view === 'kanban' ? 800 : 400 }}
                                        >
                                            <FontAwesomeIcon icon={faSquareKanban} />&nbsp;Kanban
                                        </Button>
                                    </Box>
                                </div>
                            )}
                        </Paper>
                        {view === 'list' && mainOpportunities.slice(0, pageNumber * 20).map((opportunity, i) => (
                            <OpportunityRow
                                key={opportunity.id}
                                opportunity={opportunity}
                                onOpen={onOpen}
                                accesstoken={accesstoken}
                                onUpdateOpportunity={onUpdateOpportunity}
                                accounts={accounts}
                                organization={org}
                                activeTagFilters={filter.tags}
                                exchangeRates={canSeeFinancials ? exchangeRates : null}
                            />
                        ))}
                        {view === 'kanban' && (
                            <FlowWelcomeKanban
                                opportunities={mainOpportunities}
                                accounts={accounts}
                                onOpen={onOpen}
                                onUpdateOpportunity={onUpdateOpportunity}
                                accesstoken={accesstoken}
                                org={org}
                                me={me}
                                exchangeRates={canSeeFinancials ? exchangeRates : null}
                            />
                        )}
                    </Box>
                )}
            </Box>
        </Box>
    )
}

export default FlowWelcome;
export { FlowWelcomeProps };